// SESSION-TIMELINE (A19): the pure part of the snapshots strip. The server (GET
// /live/canvas/timeline) rebuilds every state from the op log under the viewer's rights; this
// module picks what the strip shows for a scrubbed-to snapshot (read only: it never builds an
// op), where feedback on it goes, and how two versions of a block compare.
import type { SessionTimeline } from "@companyos/schema/contracts";

export type { SessionTimeline };
export type Snapshot = SessionTimeline["snapshots"][number];
export type BlockVersions = SessionTimeline["versions"][number];
export type VersionRow = BlockVersions["versions"][number];
export type FeedbackTarget = NonNullable<Snapshot["target"]>;

export const timelineKeys = {
  session: (co: string, session: string) =>
    ["company", co, "live-canvas", session, "timeline"] as const,
};

/** The snapshot to show for a scrub position: clamped, so a stale index never reads nothing. */
export function snapshotAtIndex(t: SessionTimeline, index: number | null): Snapshot | null {
  if (t.snapshots.length === 0) return null;
  const last = t.snapshots.length - 1;
  const i = index === null ? last : Math.min(Math.max(Math.trunc(index), 0), last);
  return t.snapshots[i] ?? null;
}

/** Previous and next positions for the arrow keys (null at the ends). */
export function neighbours(
  t: SessionTimeline,
  index: number,
): { prev: number | null; next: number | null } {
  const last = t.snapshots.length - 1;
  return { prev: index > 0 ? index - 1 : null, next: index < last ? index + 1 : null };
}

/** The versions of a block that a snapshot shows (the block's v-number at that moment), if shown twice. */
export function versionsAt(
  t: SessionTimeline,
  s: Snapshot,
): { blockId: string; version: number; of: number }[] {
  const out: { blockId: string; version: number; of: number }[] = [];
  for (const bv of t.versions) {
    if (!s.blocks.some((b) => b.id === bv.block_id)) continue;
    const upTo = bv.versions.filter((v) => v.snapshot_index <= s.index);
    const cur = upTo.at(-1);
    if (cur) out.push({ blockId: bv.block_id, version: cur.version, of: bv.versions.length });
  }
  return out;
}

export type VersionDiff = {
  changed: string[];
  addedFiles: string[];
  removedFiles: string[];
  same: boolean;
};

/** v1 against v2: which parts of the block differ, and which files came or went. */
export function compareVersions(a: VersionRow, b: VersionRow): VersionDiff {
  const changed: string[] = [];
  const pairs: [string, unknown, unknown][] = [
    ["title", a.block.title, b.block.title],
    ["ref", a.block.ref, b.block.ref],
    ["filter", a.block.filter, b.block.filter],
    ["kind", a.block.kind, b.block.kind],
  ];
  for (const [name, x, y] of pairs)
    if (JSON.stringify(x ?? null) !== JSON.stringify(y ?? null)) changed.push(name);
  const before = new Set(a.file_ids);
  const after = new Set(b.file_ids);
  const addedFiles = b.file_ids.filter((f) => !before.has(f));
  const removedFiles = a.file_ids.filter((f) => !after.has(f));
  return {
    changed,
    addedFiles,
    removedFiles,
    same: changed.length === 0 && addedFiles.length === 0 && removedFiles.length === 0,
  };
}

const clock = (iso: string) => iso.slice(11, 19);

/** The comment a piece of feedback becomes: the viewer's words, tied to the snapshot it was about. */
export function feedbackMarkdown(s: Snapshot, text: string): string {
  const note = `Feedback on the Live session at ${clock(s.at)} (snapshot ${s.index + 1}: ${s.summary})`;
  return `${text.trim()}\n\n_${note}_`;
}

/** A stable client id, so a double click posts one comment (messages.create is idempotent on it). */
export const feedbackClientId = (sessionId: string, s: Snapshot, text: string): string => {
  let h = 0;
  for (const ch of text.trim()) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return `tl-${sessionId.slice(0, 8)}-${s.op_id.slice(0, 8)}-${(h >>> 0).toString(36)}`;
};
